import i18n from '@/i18n/i18n'

import { accountSettingsRoute, SETTINGS_ACCOUNT_ROUTE } from '@/router/routes/settings/account-settings.routes.js'
import addressbookRoutes from '@/router/routes/settings/addressbook.routes.js'
import addressesRoutes from '@/router/routes/settings/addresses.routes.js'
import boxesRoutes from '@/router/routes/settings/boxes.routes'
import brandsRoutes from '@/router/routes/settings/brands.routes.js'
import carrierAndPricesRoute from '@/router/routes/settings/carriers-and-prices.routes.js'
import checkoutRulesRoutes from '@/router/routes/settings/checkout-rules.routes.js'
import checkoutRoutes from '@/router/routes/checkout.routes.js'
import fallbackRoute from '@/router/fallback-route.js'
import financialRoutes from '@/router/routes/settings/financial.routes.js'
import usersRoutes from '@/router/routes/settings/users.routes.js'
import integrationsRoutes from '@/router/routes/settings/integrations.routes.js'
import notifyRoutes from '@/router/routes/settings/notify.routes.js'
import ratesRoute from '@/router/routes/settings/rates.routes.js'
import returnRulesRoutes from '@/router/routes/settings/return-rules.routes.js'
import returnDefaultsRoutes from '@/router/routes/settings/return-defaults.routes.js'
import shippingRulesRoutes from '@/router/routes/settings/shipping-rules.routes.js'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { getHelpCenterUrl } from '@/services/help-center-url.service'

import {
  SETTINGS_ROUTE,
  SETTINGS_PACKGO_ROUTE,
  SETTINGS_PLANS_ROUTE,
  SETTINGS_PLANS_ROUTE_SWITCH,
  SETTINGS_OLD_PLANS_ROUTE,
  SETTINGS_PRINTING_ROUTE,
  SETTINGS_NEW_RETURNS_SETTINGS_ROUTE,
  SETTINGS_NEW_RETURNS_APPEARANCE_ROUTE,
  SETTINGS_RETURNS_ROUTE,
  SETTINGS_SHIPPING_DEFAULTS_ROUTE,
} from '@/router/routes/settings/settings.routes.names.js'

const btnHelpCenter = {
  id: 'open-help-center',
  title: () => i18n.t('Help center'),
  name: 'Help center button',
  href: getHelpCenterUrl('returnportal', 'settings'),
  newTab: true,
  mode: 'borderless',
  icon: faInfoCircle,
  align: 'right',
}

export default {
  name: SETTINGS_ROUTE,
  path: '/settings',
  redirect: { name: SETTINGS_ACCOUNT_ROUTE },
  component: () => import('@/app/settings/views/UserSettings.vue'),
  children: [
    accountSettingsRoute,
    addressbookRoutes,
    addressesRoutes,
    boxesRoutes,
    brandsRoutes,
    carrierAndPricesRoute,
    checkoutRulesRoutes,
    checkoutRoutes,
    financialRoutes,
    integrationsRoutes,
    notifyRoutes,
    ratesRoute,
    returnRulesRoutes,
    returnDefaultsRoutes,
    shippingRulesRoutes,
    usersRoutes,

    {
      name: SETTINGS_RETURNS_ROUTE,
      path: 'return-portal',
      redirect: { name: SETTINGS_NEW_RETURNS_SETTINGS_ROUTE },
      component: () => import('@/app/settings/returns/views/ReturnPortalSettingsWrapper.vue'),
      props: route => ({
        code: route.query?.code,
        state: route.query?.state,
      }),
      meta: {
        pageTitle: () => i18n.t('Return portal'),
        permissions: ['configure_returns'],
        buttons: [btnHelpCenter],
      },
      children: [
        {
          name: SETTINGS_NEW_RETURNS_SETTINGS_ROUTE,
          path: 'settings',
          component: () => import('@/app/settings/returns/views/ReturnPortalSettingsSettings.vue'),
          meta: {
            pageTitle: () => i18n.t('Return portal'),
            permissions: ['configure_returns'],
            buttons: [btnHelpCenter],
          },
        },
        {
          name: SETTINGS_NEW_RETURNS_APPEARANCE_ROUTE,
          path: 'appearance',
          component: () => import('@/app/settings/returns/views/ReturnPortalSettingsAppearance.vue'),
          meta: {
            pageTitle: () => i18n.t('Return portal'),
            permissions: ['configure_returns'],
            buttons: [btnHelpCenter],
          },
        },
      ],
    },

    {
      name: SETTINGS_PLANS_ROUTE,
      path: 'subscriptions/plans',
      component: () => import('@/app/settings/plans/views/PlansSettings.vue'),
      props: {
        isPilotPage: true,
      },
      meta: {
        pageTitle: () => i18n.t('Plans'),
        permissions: ['manage_subscriptions'],
      },
    },

    {
      name: SETTINGS_PLANS_ROUTE_SWITCH,
      path: 'subscriptions/plans-switch',
      component: () => import('@/app/settings/plans/views/PlansSettings.vue'),
      props: {
        isPilotPage: true,
        isSwitchPromotionPage: true,
      },
      meta: {
        backLinkRoute() {
          return {
            title: i18n.t('Go back'),
            to: { name: SETTINGS_PLANS_ROUTE },
          }
        },
        pageTitle: () => i18n.t('Plans'),
        permissions: ['manage_subscriptions'],
      },
    },

    {
      name: SETTINGS_OLD_PLANS_ROUTE,
      path: 'subscriptions/plans-old',
      component: () => import('@/app/settings/plans/views/PlansSettings.vue'),
      meta: {
        pageTitle: () => i18n.t('Plans'),
        permissions: ['manage_subscriptions'],
      },
    },

    {
      name: SETTINGS_SHIPPING_DEFAULTS_ROUTE,
      path: 'shipping',
      component: () => import('@/app/settings/shipping-defaults/views/ShippingDefaultsSettings.vue'),
      meta: {
        pageTitle: () => i18n.t('Shipping defaults'),
        permissions: ['configure_shipping'],
      },
    },

    {
      name: SETTINGS_PACKGO_ROUTE,
      path: 'packgo',
      component: () => import('@/app/settings/packgo/views/PackGoSettings.vue'),
      meta: {
        pageTitle: () => i18n.t('Pack & Go'),
        permissions: ['configure_packing'],
      },
    },

    {
      name: SETTINGS_PRINTING_ROUTE,
      path: '/settings/printing',
      component: () => import('@/app/settings/printing/views/PrintSettings.vue'),
      meta: {
        pageTitle: () => i18n.t('Print settings'),
        permissions: ['configure_packing'],
      },
    },

    {
      ...fallbackRoute,
      name: `${fallbackRoute.name}-settings`,
    },
    // TO DO NOT APPEND ROUTES BELOW THE FALLBACK ROUTE.
  ],
  meta: {
    pageTitle: () => i18n.t('Settings'),
    permissions: [
      /* Empty. This is just a redirect. */
    ],
  },
}
